import { useState } from "react";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { Copy, RefreshCw, Check, Pencil } from "lucide-react";

interface ChatBubbleProps {
  role: "user" | "assistant";
  content: string;
  timestamp?: Date;
  imageUrl?: string;
  onRetry?: () => void;
  showRetry?: boolean;
  onEdit?: (content: string) => void;
}

export const ChatBubble = ({ role, content, timestamp, imageUrl, onRetry, showRetry, onEdit }: ChatBubbleProps) => {
  const isUser = role === "user";
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleEdit = () => {
    onEdit?.(content);
  };
  
  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="flex flex-col"
    >
      {/* Main bubble container */}
      <div className={cn(
        "flex w-full",
        isUser ? "justify-end" : "justify-start"
      )}>
        {/* Avatar for assistant */}
        {!isUser && (
          <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center mr-2 shrink-0 mt-1">
            <span className="text-primary-foreground text-xs font-bold">S</span>
          </div>
        )}
        
        <div className={cn(
          "max-w-[80%] rounded-3xl px-5 py-3",
          isUser 
            ? "bg-primary text-primary-foreground rounded-br-lg" 
            : "bg-secondary text-secondary-foreground rounded-bl-lg"
        )}>
          {imageUrl && (
            <img 
              src={imageUrl} 
              alt="Uploaded hazard" 
              className="rounded-2xl mb-3 max-w-full h-auto max-h-48 object-cover"
            />
          )}
          <p className="text-[15px] leading-relaxed whitespace-pre-wrap">{content}</p>
          {timestamp && (
            <p className={cn(
              "text-xs mt-2",
              isUser ? "text-primary-foreground/70" : "text-muted-foreground"
            )}>
              {timestamp.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })}
            </p>
          )}
        </div>
      </div>
      
      {/* Action buttons BELOW bubble, aligned based on role */}
      <div className={cn(
        "flex gap-1 mt-1",
        isUser ? "justify-end pr-2" : "justify-start pl-12"
      )}>
        {/* Edit button - only for user messages */}
        {isUser && onEdit && (
          <button 
            onClick={handleEdit}
            className="text-muted-foreground/50 hover:text-muted-foreground p-1 rounded transition-colors"
            title="Edit pesan"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        )}
        
        {/* Copy button - for all messages */}
        <button 
          onClick={handleCopy}
          className="text-muted-foreground/50 hover:text-muted-foreground p-1 rounded transition-colors"
          title={copied ? 'Disalin!' : 'Salin'}
        >
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
        </button>
        
        {/* Retry button - for assistant messages when showRetry is true */}
        {!isUser && showRetry && onRetry && (
          <button 
            onClick={onRetry}
            className="text-muted-foreground/50 hover:text-muted-foreground p-1 rounded transition-colors"
            title="Coba lagi"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </motion.div>
  );
};
